/**
 * @desc        Serves as a conversion of codes to match services and API parameters.
 * @version     1.0 - Jun 22 **/

/**
 * Converts language code defined in user preference to DarkSky API format.
 * @param {*} preferenceLangCode language code as defined in the B/E
 * @returns {string} language code as defined in momentjs
 */
export const localeToDarkSky = preferenceLangCode => {
    switch (preferenceLangCode) {
        case 'es-ar':
            return 'es' // Spanish
        case 'in':
        case 'hi':
            return 'hi' // Hindi
        case 'pt-br':
            return 'pt' // Portuguese
        case 'en-au':
        case 'en-ca':
        case 'en-nz':
        case 'en-gb':
        case 'en-us':
        case 'en-za':
        default:
            return 'en'
    }
}

/**
 * Converts language code defined in user preference, to OpenWeather API format.
 *
 * @param {*} preferenceLangCode
 * @returns {string} Language code for the OpenWeather API
 */
export const localeToOpenWeather = preferenceLangCode => {
    switch (preferenceLangCode) {
        case 'in':
            return 'hi'
        case 'es-ar':
            return 'es'
        case 'pt-br':
            return 'pt_br'
        case 'hi':
            return preferenceLangCode // No change
        case 'en-au':
        case 'en-ca':
        case 'en-nz':
        case 'en-gb':
        case 'en-us':
        case 'en-za':
        default:
            return 'en'
    }
}

/**
 * Converts language code defined in user preference to momentjs format.
 * @param {*} preferenceLangCode language code as defined in the B/E
 * @returns {string} language code as defined in momentjs
 */
export const localeToMoment = preferenceLangCode => {
    switch (preferenceLangCode) {
        case 'es-ar':
            return 'es' // Spanish
        case 'in':
        case 'hi':
            return 'hi' // Hindi
        case 'pt-br':
            return 'pt' // Portuguese
        case 'en-au':
        case 'en-ca':
        case 'en-gb':
        case 'en-nz':
            return preferenceLangCode // no change
        case 'en-us':
        case 'en-za':
        default:
            return 'en-ca' // no support for en, en-us nor en-za in momentjs
    }
}

/**
 * Converts language code defined in user preference, to an Intl API
 * (ECMAScript Internationalization API) locale.
 *
 * @param {*} preferenceLangCode
 * @returns {string} Language code for the Intl API
 */
export const localeToIntlApiLocale = preferenceLangCode => {
    switch (preferenceLangCode) {
        case 'in':
            return 'hi-IN'
        case 'es-ar':
        case 'hi':
        case 'pt-br':
        case 'en-au':
        case 'en-ca':
        case 'en-nz':
        case 'en-gb':
        case 'en-us':
        case 'en-za':
            return preferenceLangCode // No change
        default:
            return 'en'
    }
}

/**
 * Converts language code defined in user preference to i18next recognised format which matches the naming of files and is supported by Date.toLocaleString().
 * @param {*} preferenceLangCode language code as defined in the B/E
 * @returns {string} language code as defined in i18next translations paths, usually means the second part is capitalised
 */
export const localeToi18next = preferenceLangCode => {
    switch (preferenceLangCode) {
        case 'es-ar':
            return 'es-AR' // Spanish
        case 'in':
        case 'hi':
            return 'in' // Hindi
        case 'pt-br':
            return 'pt-BR' // Portuguese
        case 'en-au':
        case 'en-ca':
        case 'en-gb':
        case 'en-nz':
        case 'en-us':
        case 'en-za':
        default:
            return 'en-AU'
    }
}

/**
 * Converts language code defined in user preference, to ArcGIS API format.
 *
 * @param {string} preferenceLangCode
 * @returns {string} Language code for the ArcGIS API
 */
export const localeToArcgis = preferenceLangCode => {
    switch (preferenceLangCode) {
        case 'es-ar':
            return 'es'
        case 'pt-br':
            return 'pt-BR'
        case 'en-au':
        case 'en-nz':
        case 'en-gb':
        case 'en-za':
        case 'in': // ArcGIS doesn't support Hindi yet :(
        case 'hi': // ArcGIS doesn't support Hindi yet :(
            return 'en-GB'
        case 'en-us':
            return 'en-US'
        case 'en-ca':
        default:
            return 'en'
    }
}
